import { Service, PLATFORM_ID, inject } from '@angular/core';
import { DrupalService } from '@app/services/drupal.service';
import { isPlatformBrowser } from '@angular/common';
import Cookies from 'js-cookie';
import { environment } from '@src/environments/environment';

/**
 * Routes users through the Fastly bot-detection challenge before they can submit a webform.
 *
 * Why: `POST /webform_rest/submit` is blocked (406) by the Fastly WAF unless the request
 * carries a `_fs_ch_cp_*` cookie, which is only set by passing a GET challenge during a real
 * browser navigation. The Angular submit flow is pure AJAX, so a user who never navigated
 * through a challenge has no cookie. When a webform renders without the cookie, we send the
 * user (via a real navigation) to an uncached challenge path carrying `?destination=<current>`;
 * after the challenge sets the cookie, `handleReturn()` brings them back.
 *
 * The challenge cookie is HttpOnly, so we detect it via a non-HttpOnly companion cookie set by
 * the server-side `/challenge-signal` endpoint (which reads the raw cookie header).
 */
const COMPANION_COOKIE = 'mobi_fs_ch_ok';
const ATTEMPT_MARKER = 'mobi_fs_ch_attempted';
const SIGNAL_ENDPOINT = '/challenge-signal';

@Service()
export class FastlyChallengeService {
  #platformId = inject(PLATFORM_ID);
  #drupal = inject(DrupalService);
  // Per-document cache of the async signal result.
  #signalChecked = false;
  #signalOk = false;

  get challengePath(): string {
    return environment.fastlyChallengePath;
  }

  isEnabled(): boolean {
    return (
      isPlatformBrowser(this.#platformId) && environment.enableFastlyChallenge === true
    );
  }

  isOnChallengePath(pathname: string): boolean {
    return pathname.startsWith(this.challengePath);
  }

  hasCompanion(): boolean {
    return Cookies.get(COMPANION_COOKIE) === '1';
  }

  /**
   * Called when a webform renders. Redirects to the challenge if the cookie is missing.
   * Returns true if a redirect was triggered (caller should stop rendering).
   */
  async maybeRedirectOnLoad(): Promise<boolean> {
    if (!this.isEnabled()) return false;
    const { search } = window.location;
    const pathname = this.#drupal.route()?.path || window.location.pathname;

    if (this.isOnChallengePath(pathname)) return false;

    if (await this.#isChallengeOk()) {
      this.#setAttempted(false); // success → clear guard so a later cookie expiry can re-challenge
      return false;
    }
    // Loop guard: we already sent the user through a challenge this session and still have no
    // cookie (challenge failed / blocked). Don't bounce again — let the page render.
    if (this.#alreadyAttempted()) return false;

    this.#setAttempted(true);
    const dest = encodeURIComponent(pathname + search);
    window.location.assign(`${this.challengePath}?destination=${dest}`);
    return true;
  }

  /**
   * Called once on app init. On the challenge landing page, returns the user to `?destination=`
   * once the challenge cookie is confirmed.
   */
  async handleReturn(): Promise<void> {
    if (!this.isEnabled()) return;
    const destination = new URLSearchParams(window.location.search).get('destination');
    if (!destination || !this.#isSafeInternalPath(destination)) return;

    if (await this.#isChallengeOk()) {
      this.#setAttempted(false);
      window.location.assign(destination);
    }
  }

  /**
   * Submit-time fallback: `/session/token` returned challenge content instead of a token, so the
   * cookie is missing/expired. Drop any stale companion and re-challenge.
   */
  revalidateAndChallenge(): void {
    if (!isPlatformBrowser(this.#platformId)) return;
    this.#clearCompanion();
    this.#signalChecked = false;
    this.#signalOk = false;
    void this.maybeRedirectOnLoad();
  }

  async #isChallengeOk(): Promise<boolean> {
    if (this.hasCompanion()) return true;
    if (this.#signalChecked) return this.#signalOk;
    this.#signalOk = await this.#fetchSignal();
    this.#signalChecked = true;
    return this.#signalOk;
  }

  async #fetchSignal(): Promise<boolean> {
    try {
      const res = await fetch(SIGNAL_ENDPOINT, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return false; // blocked/challenged → treat as not challenged
      const data = await res.json();
      return data?.challengeOk === true;
    } catch {
      return false;
    }
  }

  #isSafeInternalPath(path: string): boolean {
    return path.startsWith('/') && !path.startsWith('//');
  }

  #alreadyAttempted(): boolean {
    try {
      return window.sessionStorage.getItem(ATTEMPT_MARKER) === '1';
    } catch {
      return false;
    }
  }

  #setAttempted(value: boolean): void {
    try {
      if (value) window.sessionStorage.setItem(ATTEMPT_MARKER, '1');
      else window.sessionStorage.removeItem(ATTEMPT_MARKER);
    } catch {
      // sessionStorage unavailable (private mode / blocked) — loop guard simply no-ops.
    }
  }

  #clearCompanion(): void {
    try {
      Cookies.remove(COMPANION_COOKIE, { path: '/' });
    } catch {
      // ignore
    }
  }
}
